type Path = string | readonly string[];

const toSegments = (path: Path): string[] => (typeof path === 'string' ? path.split('.').filter(Boolean) : [...path]);

// =====================================
// MIGRATION APIs
// =====================================

export const addNewField = (target: Record<string, unknown>, path: Path, defaultValue: unknown): void => {
    if (getAtPath(target, path) !== undefined) return;

    const location = ensureParentObject(target, path);
    if (!location) return;
    location.parent[location.key] = defaultValue;
};

export const replaceField = (target: Record<string, unknown>, path: Path, nextValue: unknown): void => {
    const location = ensureParentObject(target, path);
    if (!location) return;
    location.parent[location.key] = nextValue;
};

export const removeOldField = (target: Record<string, unknown>, path: Path): void => {
    deleteAtPath(target, path);
};

export const renameField = (
    target: Record<string, unknown>,
    fromPath: Path,
    toPath: Path,
    mapValue?: (value: unknown) => unknown
): void => {
    const currentValue = getAtPath(target, fromPath);
    if (currentValue === undefined) return;

    replaceField(target, toPath, mapValue ? mapValue(currentValue) : currentValue);
    removeOldField(target, fromPath);
};

// =====================================
// HELPERS
// =====================================

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const getAtPath = (value: unknown, path: Path): unknown => {
    let current: unknown = value;
    for (const segment of toSegments(path)) {
        if (!isPlainObject(current) || !(segment in current)) return undefined;
        current = current[segment];
    }
    return current;
};

const ensureParentObject = (target: Record<string, unknown>, path: Path): { parent: Record<string, unknown>; key: string } | null => {
    const segments = toSegments(path);
    if (segments.length === 0) return null;

    let current = target;
    for (let i = 0; i < segments.length - 1; i += 1) {
        const segment = segments[i];
        const next = current[segment];
        if (!isPlainObject(next)) {
            current[segment] = {};
        }
        current = current[segment] as Record<string, unknown>;
    }

    return {
        parent: current,
        key: segments[segments.length - 1],
    };
};

const deleteAtPath = (target: Record<string, unknown>, path: Path): void => {
    const segments = toSegments(path);
    if (segments.length === 0) return;

    let current: Record<string, unknown> = target;
    for (let i = 0; i < segments.length - 1; i += 1) {
        const next = current[segments[i]];
        if (!isPlainObject(next)) return;
        current = next;
    }

    delete current[segments[segments.length - 1]];
};
